import React from 'react';
import { Flex, IconButton, useBreakpointValue } from '@chakra-ui/react';
import { ClerkDocsLogo } from '@components/common';
import { XIcon } from '@heroicons/react/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { DocsNavigation } from '@types';
import { NavigationContent } from './navigation-content';
import { NavigationConfiguration } from '@lib/docs/types';

const MotionFlex = motion(Flex);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  navigation?: Array<DocsNavigation>;
  mdxNav?: NavigationConfiguration;
};

export function MobileMenu({
  isOpen,
  onClose,
  navigation,
  mdxNav
}: Props): JSX.Element {
  const isMobileHeaderVisible = useBreakpointValue({ base: true, lg: false });

  React.useEffect(() => {
    // In case the mobile menu is open and the screen is resized to desktop viewport (Chakra's `lg` breakpoint):
    // 1. Close menu
    // 2. `useEffect` below will re-run
    // 3. Add `overflowY = 'auto'` to `body`
    if (isOpen && !isMobileHeaderVisible) {
      onClose();
    }
  }, [isMobileHeaderVisible, isOpen, onClose]);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }

    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOpen, isMobileHeaderVisible]);

  return (
    <AnimatePresence>
      {isOpen && (
        <MotionFlex
          direction="column"
          w="100%"
          bg="var(--bodyBg)"
          minH="100vh"
          overflow="hidden"
          pos="absolute"
          top={0}
          left={0}
          zIndex="modal"
          px={6}
          transition={{ duration: 0.1 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Flex align="center" justify="space-between" py={1}>
            <ClerkDocsLogo />

            <IconButton
              aria-label="Close menu"
              color="gray.400"
              icon={<XIcon height="100%" />}
              boxSize={6}
              size="xs"
              variant="ghost"
              _hover={{ bgColor: 'unset', color: 'gray.500' }}
              _active={{ bgColor: 'unset' }}
              onClick={onClose}
            />
          </Flex>

          <Flex flex={1} pt={4} pb={10}>
            <NavigationContent
              navigation={navigation}
              mdxNav={mdxNav}
              isMobileMenuOpen={isOpen}
              closeMobileMenu={onClose}
            />
          </Flex>
        </MotionFlex>
      )}
    </AnimatePresence>
  );
}
